// Modules
import { connect } from 'react-redux';
import Promise from 'bluebird';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';

import { Element } from 'components/forms/form-elements';
import { Page, PageHeader, PageContent, ActionSection } from 'components/page';
import { PrimaryButton } from 'components/widgets/buttons';
import { Row } from 'components/forms/form-structure';
import { alerts } from 'modules/alerts/actions';
import { convertArrayToOptions } from 'utils/input-utils';
import { didQueryUpdate } from 'utils/filter-utils';
import CheckableActions from 'components/content-containers/tables/checkable-actions';
import Filters from 'modules/filters/filters';
import Pager from 'components/widgets/pager/pager';
import PeopleTable from 'components/models/person/people-table';
import Person from 'services/data/person';
import Select from 'components/forms/inputs/select';
import exportCSV from 'utils/export-csv';
import replaceById from 'utils/replace-by-id';

import * as actions from './store/actions';


// Component
class People extends React.Component {
  static propTypes = {
    people: PropTypes.array,
    fetching: PropTypes.bool,
    count: PropTypes.number,
    query: PropTypes.object,
    tag: PropTypes.string,
    settings: PropTypes.object
  };

  constructor() {
    super();

    this.state = {};

    this.search = this.search.bind(this);
    this.export = this.export.bind(this);
    this.addTag = this.addTag.bind(this);
    this.update = people => actions.set({ people });
    this.setTag = selectedTag => actions.set({ selectedTag });

    this.filterConfig = {
      basic: ['search', 'company'],
      advanced: [
        ['search', 'company', 'personRole'],
        ['personStatus', 'tags']
      ],
      defaults: { personStatus: ['Active'] }
    };
  }

  componentWillReceiveProps(nextProps) {
    if (didQueryUpdate(nextProps, this.props)) this.search(nextProps.query);
  }

  search(query) {
    actions.set({ fetching: true });
    const fullQuery = Person.formatQuery(query || this.props.query);
    
    Promise.props({
      people: Person.find(fullQuery),
      count: Person.count({ where: fullQuery.where })
    })
      .then((response) => {
        actions.set({ fetching: false, count: response.count, people: response.people });
      })
      .catch(alerts.catchError);
  }

  addTag() {
    const { people, tag } = this.props;
    const checked = _.filter(people, 'checked');

    if (!tag || !checked || !checked.length) return;

    const updated = _.map(checked, person => ({ ...person, tags: _(person.tags || []).concat(tag).uniq().value() }));
    const serverUpdate = _.map(updated, person => _.pick(person, ['id', 'tags']));

    this.update(replaceById(updated, people));

    return Person.updateMany(serverUpdate);
  }

  export() {
    const fullQuery = Person.formatQuery(this.props.query);
    delete fullQuery.limit;

    Person.find(fullQuery)
      .then((people) => {
        const header = ['First Name', 'Last Name', 'Primary Email', 'Mobile Number'];

        const data = _.map(people, person => [
          _.get(person, 'firstName', ''),
          _.get(person, 'lastName', ''),
          _.get(person, 'primaryEmail.email', ''),
          _.get(_.find(person.phoneNumbers, { type: 'Mobile' }), 'number', '')
        ]);

        exportCSV(data, 'people.csv', { header });
      })
      .catch(alerts.catchError);
  }

  render() {
    const { people, fetching, count, settings, tag } = this.props;
    const anyChecked = _.some(people, 'checked');

    const headerActions = (
      <div className="header-actions">
        <PrimaryButton onClick={this.export} disabled={_.isEmpty(people)}>Export</PrimaryButton>
      </div>
    );

    return (
      <Page>
        <PageHeader title="People" actions={headerActions}>
          <ActionSection>
            <Filters className={anyChecked ? 'hidden' : ''} config={this.filterConfig} />
            { anyChecked &&
              <CheckableActions list={people} update={this.update}>
                <Row className="no-divider">
                  <Element>
                    <Select value={tag} placeholder="Select tag..." onChange={this.setTag} options={convertArrayToOptions(settings.personTags)} />
                  </Element>
                  <Element>
                    <PrimaryButton onClick={this.addTag}>Add Tag</PrimaryButton>
                  </Element>
                </Row>
              </CheckableActions>
            }
          </ActionSection>
        </PageHeader>
        <PageContent
          fetching={fetching && _.isEmpty(people)}
          emptyText="No people, adjust your filters!"
          emptyTextIcon="svg/filter.svg"
          showEmptyText={!fetching && _.isEmpty(people)}
        >
          
          <PeopleTable people={people} update={this.update} />
          <Pager count={count} />
        </PageContent>
      </Page>
    );
  }
}

// Exports
export default connect((state) => ({
  people: state.people.people,
  fetching: state.people.fetching,
  tag: state.people.selectedTag,
  count: state.people.count,
  query: state.location.query,
  settings: state.pageData.settings
}))(People);
